/**
 * ITER303.1 · Design Picker Modal / Popover
 *
 * Grid of design thumbnails combining tenant-owned saved_designs AND
 * seed templates.  Used by:
 *   • Voucher Studio "Load Saved Design" dropdown
 *   • Voucher Types table bulk-apply action bar
 *   • Voucher Types table per-row paintbrush quick-apply
 *
 * All three surfaces call the same `onPick(designObj, source)` callback.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import VoucherCard from './VoucherCard';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Individual design thumbnail — defined outside the parent so React
 * doesn't tear down + remount on every parent render.  Purely
 * presentational; the parent owns selection + delete state.
 */
function DesignThumb({
  item, source, active, onSelect, onDelete,
  outletName, outletLogo, outletInitials,
}) {
  // ITER303.1-fix · Outer must NOT be <button> because the delete
  // affordance is itself a clickable element; nested interactives are
  // invalid HTML and warn in Safari.  A <div role="button"> with an
  // onKeyDown handler retains keyboard accessibility.
  const activate = (e) => { e.preventDefault(); onSelect?.(); };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') activate(e);
      }}
      data-testid={`design-pick-${source}-${item.id}`}
      className={`relative flex flex-col items-center gap-1.5 p-2 rounded-lg
                  cursor-pointer outline-none focus-visible:ring-2
                  focus-visible:ring-[color:var(--color-primary,#143E30)]
                  transition-all ${active
                    ? 'ring-2 ring-offset-1 ring-[color:var(--color-primary,#143E30)] bg-slate-50'
                    : 'hover:bg-slate-50 border border-transparent'}`}
    >
      {source === 'saved' && onDelete && (
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); onDelete(e); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault(); e.stopPropagation(); onDelete(e);
            }
          }}
          data-testid={`design-delete-${item.id}`}
          className="absolute -top-1 -right-1 z-10 p-1 rounded-full bg-white
                     border border-slate-200 opacity-60 hover:opacity-100
                     hover:text-red-600 hover:border-red-200 transition
                     cursor-pointer"
        >
          <Trash2 className="w-3 h-3" />
        </span>
      )}
      <div className="overflow-hidden rounded-md"
           style={{ width: 110, height: 154 }}>
        <div className="origin-top-left"
             style={{ transform: 'scale(0.34)', width: 324, height: 453 }}>
          <VoucherCard
            voucher={{ name: item.name, benefit_value: 500,
                       benefit_type: 'flat_discount', code: 'SAMPLE' }}
            design={item.design}
            outletName={outletName}
            outletLogo={outletLogo}
            outletInitials={outletInitials}
            size="full"
            showTerms={false}
          />
        </div>
      </div>
      <div className="text-[10px] font-medium text-slate-700
                      max-w-[110px] truncate text-center">
        {item.name}
      </div>
    </div>
  );
}

export default function DesignPickerModal({
  open, onOpenChange, onPick, headers,
  outletName = 'Restaurant', outletLogo = null, outletInitials = 'RP',
  title = 'Choose a design',
  description = 'Pick from your saved designs or the built-in templates.',
  confirmLabel = 'Apply',
}) {
  const [savedDesigns, setSavedDesigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);  // { source: 'saved'|'template', id, design, name }

  const load = async () => {
    setLoading(true);
    try {
      const [sd, tp] = await Promise.all([
        axios.get(`${API}/api/admin/loyalty/saved-designs`, { headers }),
        axios.get(`${API}/api/admin/loyalty/voucher-templates`, { headers }),
      ]);
      setSavedDesigns(sd.data?.designs || []);
      setTemplates(tp.data?.templates || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load designs');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) { setSelected(null); load(); }
  }, [open]);  // load ref is stable in this component; safe to omit

  const deleteSaved = async (id, ev) => {
    ev.stopPropagation();
    if (!window.confirm('Delete this saved design?')) return;
    try {
      await axios.delete(`${API}/api/admin/loyalty/saved-designs/${id}`, { headers });
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const confirm = () => {
    if (!selected) { toast.error('Pick a design first'); return; }
    onPick?.(selected.design, selected.source, selected);
    onOpenChange?.(false);
  };

  const setActive = (source, item) => setSelected({
    source,
    id: item.id,
    // ITER303.1-fix · Merge the source id into the design payload so
    // downstream `voucher_type.design.template_id` (or a future
    // saved_design_id) is preserved for traceability.
    design: source === 'template'
      ? { ...(item.design || {}), template_id: item.id }
      : (item.design || {}),
    name: item.name,
  });
  const isActive = (source, id) =>
    selected?.source === source && selected?.id === id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-sm text-slate-500">Loading…</div>
        ) : (
          <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-2">
            {savedDesigns.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">
                  My Saved Designs
                </div>
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                  {savedDesigns.map((d) => (
                    <DesignThumb key={d.id} item={d} source="saved"
                                  active={isActive('saved', d.id)}
                                  onSelect={() => setActive('saved', d)}
                                  onDelete={(ev) => deleteSaved(d.id, ev)}
                                  outletName={outletName}
                                  outletLogo={outletLogo}
                                  outletInitials={outletInitials} />
                  ))}
                </div>
              </div>
            )}
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">
                Templates
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                {templates.map((t) => (
                  <DesignThumb key={t.id} item={t} source="template"
                                active={isActive('template', t.id)}
                                onSelect={() => setActive('template', t)}
                                outletName={outletName}
                                outletLogo={outletLogo}
                                outletInitials={outletInitials} />
                ))}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="border-t pt-3">
          <div className="flex-1 text-[11px] text-slate-500 self-center">
            {selected ? <>Selected: <span className="font-medium text-slate-700">{selected.name}</span></> : 'No design selected'}
          </div>
          <Button variant="outline" onClick={() => onOpenChange?.(false)}
                  data-testid="design-picker-cancel">Cancel</Button>
          <Button onClick={confirm}
                  data-testid="design-picker-confirm"
                  disabled={!selected}
                  className="bg-[color:var(--color-primary)] text-white
                             hover:bg-[color:var(--color-primary-hover)]">
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
